import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Trophy, ArrowRight, Briefcase, Building2, Target, TrendingUp,
  Handshake, Users, Award, Heart, ShieldCheck, MapPin, DollarSign,
  Loader2, CheckCircle2, Sparkles, Globe, Megaphone, Landmark, GraduationCap,
  ChevronLeft, ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  'Financial Services', 'Real Estate', 'Legal', 'Healthcare', 'Technology',
  'Insurance', 'Automotive', 'Retail', 'Restaurant / Hospitality', 'Golf Equipment / Apparel',
  'Construction / Trades', 'Consulting', 'Marketing / Media', 'Beverage', 'Non-profit', 'Other'
];

const INTEREST_OPTIONS = [
  'Corporate Golf Outings', 'Charity Events', 'Fundraisers', 'Youth Golf',
  "Women's Golf", 'Veteran Events', 'Networking Events', 'Golf Leagues',
  'Golf Travel', 'Other'
];

const COVERAGE_OPTIONS = ['National', 'Regional', 'State', 'County', 'City', 'Zip Code'];

const BUDGET_OPTIONS = [
  'Under $500', '$500 - $1,000', '$1,000 - $2,500', '$2,500 - $5,000',
  '$5,000 - $10,000', '$10,000+', 'Custom'
];

const SPONSORSHIP_TYPES = [
  'Title Sponsor', 'Presenting Sponsor', 'Gold Sponsor', 'Silver Sponsor',
  'Hole Sponsor', 'Longest Drive Sponsor', 'Closest to the Pin Sponsor',
  'Beverage Cart Sponsor', 'Breakfast Sponsor', 'Lunch Sponsor', 'Dinner Sponsor',
  'Gift Bag Sponsor', 'Prize Sponsor', 'Driving Range Sponsor',
  'Putting Contest Sponsor', 'Product Sponsor', 'Contest Sponsor',
  'Golf Cart Sponsor', 'Custom Sponsorship'
];

const BUSINESS_GOALS = [
  'Lead Generation', 'Brand Awareness', 'Networking', 'Community Engagement',
  'Recruiting', 'Employee Engagement', 'Customer Appreciation', 'Other'
];

const OPPORTUNITY_TILES = [
  { name: 'Title Sponsor', desc: 'Headline the event — logo on everything.', icon: Trophy, tier: 'top' },
  { name: 'Presenting Sponsor', desc: 'Prime brand placement on all materials.', icon: Award, tier: 'top' },
  { name: 'Gold Sponsor', desc: 'Premium logo placement & speaking slot.', icon: Sparkles, tier: 'gold' },
  { name: 'Silver Sponsor', desc: 'Visible signage & digital promotion.', icon: ShieldCheck, tier: 'silver' },
  { name: 'Hole Sponsor', desc: 'Own a hole — signage + on-course activation.', icon: MapPin, tier: 'std' },
  { name: 'Longest Drive Sponsor', desc: 'On-course contest branding.', icon: Target, tier: 'std' },
  { name: 'Closest to the Pin', desc: 'On-course contest branding.', icon: Target, tier: 'std' },
  { name: 'Beverage Cart Sponsor', desc: 'Every sip carries your brand.', icon: Handshake, tier: 'std' },
  { name: 'Breakfast Sponsor', desc: 'Start every golfer\'s day with your brand.', icon: Sparkles, tier: 'std' },
  { name: 'Lunch Sponsor', desc: 'Feed 100+ decision-makers.', icon: Sparkles, tier: 'std' },
  { name: 'Dinner Sponsor', desc: 'Own the awards banquet.', icon: Sparkles, tier: 'std' },
  { name: 'Gift Bag Sponsor', desc: 'Product samples in every bag.', icon: Briefcase, tier: 'std' },
  { name: 'Prize Sponsor', desc: 'Provide winner prizes & recognition.', icon: Trophy, tier: 'std' },
  { name: 'Driving Range Sponsor', desc: 'Warm-up branding for every player.', icon: Target, tier: 'std' },
  { name: 'Putting Contest', desc: 'High-visibility interactive activation.', icon: Target, tier: 'std' },
  { name: 'Product Sponsor', desc: 'Bring your products directly to golfers.', icon: Briefcase, tier: 'std' },
  { name: 'Custom Sponsorship', desc: 'Build a package that fits your goals.', icon: Sparkles, tier: 'gold' },
];

const WHY_BENEFITS = [
  { icon: Users, title: 'Reach golfers & business pros', copy: 'Bundle members are decision-makers, executives, and community leaders.' },
  { icon: Megaphone, title: 'Build brand awareness', copy: 'Get in front of thousands of golfers across the country.' },
  { icon: TrendingUp, title: 'Generate new business leads', copy: 'Every event is a face-to-face networking opportunity.' },
  { icon: Heart, title: 'Support charities & nonprofits', copy: 'Give back while marketing your brand.' },
  { icon: Handshake, title: 'Network with decision-makers', copy: 'Meet founders, executives, and community leaders.' },
  { icon: Globe, title: 'Local or national events', copy: 'From backyard fundraisers to major tournaments.' },
  { icon: Landmark, title: 'Corporate tournaments', copy: 'Access professional and corporate-hosted outings.' },
  { icon: GraduationCap, title: 'Youth golf programs', copy: 'Sponsor the next generation of golfers.' },
];

const STEP_LABELS = ['Company', 'Interests', 'Coverage', 'Budget & Types', 'Review'];

export default function SponsorLanding() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    company_name: '', industry: '', business_description: '', website: '',
    business_address: '', primary_contact: '', phone: '', email: '',
    interests: [],
    geographic_coverage: '', coverage_states: [], travel_radius: '',
    budget: '', sponsorship_types: [], business_goals: [],
  });

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggle = (k, v) => setForm(prev => ({
    ...prev,
    [k]: prev[k].includes(v) ? prev[k].filter(x => x !== v) : [...prev[k], v]
  }));

  const isStepValid = () => {
    if (step === 0) return form.company_name && form.industry && form.primary_contact && form.email;
    if (step === 1) return form.interests.length > 0;
    if (step === 2) return !!form.geographic_coverage;
    if (step === 3) return !!form.budget && form.sponsorship_types.length > 0;
    return true;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/sponsors/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        toast.success('Application submitted!');
      } else {
        toast.error(data?.detail || 'Failed to submit');
      }
    } catch (e) {
      toast.error('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f0b] text-white" data-testid="sponsor-landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0b0f0b]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80" data-testid="sponsor-nav-home">
            <ArrowLeft className="w-4 h-4 text-emerald-400" />
            <span className="font-black text-sm tracking-tight">THE BUNDLE GOLF CLUB</span>
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/sponsor-marketplace')} data-testid="sponsor-nav-marketplace" className="text-gray-300 hover:text-white text-xs px-3 py-1.5">Browse Opportunities</button>
            <button onClick={() => setShowForm(true)} data-testid="sponsor-nav-apply" className="bg-blue-500 hover:bg-blue-400 text-white font-bold text-xs px-4 py-1.5 rounded-lg transition-colors">Become a Sponsor</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 via-transparent to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest mb-6">
              <Briefcase className="w-3 h-3" /> Sponsor Marketplace
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6">
              Grow Your Business<br />
              <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">Through Golf</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl">
              Bundle Golf Club connects sponsors with golf tournaments, charity outings, corporate events, and fundraising organizations across the country. Whether your goal is brand awareness, lead generation, networking, or community engagement — we&apos;ll help you connect with the right events.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setShowForm(true)} data-testid="sponsor-hero-apply" className="bg-blue-500 hover:bg-blue-400 text-white font-bold px-6 py-6 rounded-xl text-sm">
                Become a Sponsor <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => navigate('/sponsor-marketplace')} data-testid="sponsor-hero-browse" variant="outline" className="border-white/20 text-white hover:bg-white/10 font-semibold px-6 py-6 rounded-xl text-sm">
                Browse Sponsorship Opportunities
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY SPONSOR */}
      <section className="py-16 bg-[#0e140e]" data-testid="sponsor-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-blue-400 text-xs font-bold uppercase tracking-widest">Why Sponsor Through Bundle</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-2">Golf is the boardroom of the outdoors.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY_BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="p-5 rounded-2xl bg-[#131a13] border border-white/5 hover:border-blue-500/30 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-sm mb-1">{b.title}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{b.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section className="py-16" data-testid="sponsor-opportunities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Sponsorship Opportunities</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-2">Every level, every event</h2>
            <p className="text-sm text-gray-400 mt-3 max-w-2xl mx-auto">Pick a tier that matches your budget and marketing goals. Tournament organizers can offer any combination below.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {OPPORTUNITY_TILES.map((o, i) => {
              const Icon = o.icon;
              const accent = o.tier === 'top' ? 'from-blue-600/20 to-emerald-600/10 border-blue-500/40' :
                             o.tier === 'gold' ? 'from-yellow-600/20 to-amber-600/10 border-yellow-500/40' :
                             o.tier === 'silver' ? 'from-gray-500/20 to-gray-600/10 border-gray-400/30' :
                             'from-white/5 to-white/0 border-white/10';
              return (
                <div key={i} className={`p-4 rounded-xl bg-gradient-to-br ${accent} border hover:scale-[1.02] transition-transform`}>
                  <Icon className="w-5 h-5 mb-2 text-blue-300" />
                  <h3 className="font-bold text-sm mb-1">{o.name}</h3>
                  <p className="text-[11px] text-gray-400 leading-relaxed">{o.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Button onClick={() => navigate('/sponsor-marketplace')} data-testid="sponsor-opps-browse" className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 py-5 rounded-xl text-sm">
              Browse Live Sponsorship Opportunities <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-b from-blue-900/20 to-[#0b0f0b]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4">Ready to connect with events that match your brand?</h2>
          <p className="text-sm text-gray-300 mb-8">Apply once, get matched with opportunities across our national network.</p>
          <Button onClick={() => setShowForm(true)} data-testid="sponsor-bottom-apply" className="bg-blue-500 hover:bg-blue-400 text-white font-bold px-8 py-6 rounded-xl text-base">
            Become a Sponsor <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 bg-[#080b08]">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-xs text-gray-500">&copy; 2026 The Bundle Golf Club</span>
          <div className="flex gap-5 text-xs text-gray-500">
            <a href="/" className="hover:text-white">Home</a>
            <a href="/become-admin" className="hover:text-white">Directors</a>
            <a href="/fundraiser" className="hover:text-white">Fundraisers</a>
            <a href="/sponsor-marketplace" className="hover:text-white">Marketplace</a>
          </div>
        </div>
      </footer>

      {/* Application Form Overlay */}
      {showForm && (
        <div className="fixed inset-0 z-[100] bg-[#0b0f0b]/95 backdrop-blur-md overflow-y-auto" data-testid="sponsor-application-form">
          <div className="min-h-screen py-8 px-4">
            <div className="max-w-3xl mx-auto bg-[#131a13] border border-white/10 rounded-3xl p-6 sm:p-10">
              {submitted ? (
                <div className="text-center py-12" data-testid="sponsor-application-success">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h2 className="text-2xl font-black mb-3">Application Submitted!</h2>
                  <p className="text-gray-300 mb-6 max-w-md mx-auto">A Bundle director will review your application within 1-2 business days. You&apos;ll get an email with login credentials once approved.</p>
                  <Button onClick={() => { setShowForm(false); navigate('/'); }} className="bg-blue-500 hover:bg-blue-400 text-white font-bold px-6 py-5 rounded-xl">
                    Back to Home
                  </Button>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-blue-400">Sponsor Application</div>
                      <h2 className="text-2xl font-black mt-1">{STEP_LABELS[step]}</h2>
                    </div>
                    <button onClick={() => setShowForm(false)} data-testid="sponsor-form-close" className="text-gray-400 hover:text-white text-sm">Close</button>
                  </div>

                  {/* Step indicator */}
                  <div className="flex gap-1 mb-8">
                    {STEP_LABELS.map((_, i) => (
                      <div key={i} className={`h-1 flex-1 rounded ${i <= step ? 'bg-blue-500' : 'bg-white/10'}`} />
                    ))}
                  </div>

                  {/* Step content */}
                  {step === 0 && (
                    <div className="space-y-4">
                      <Field label="Company Name *"><Input data-testid="sponsor-field-company" value={form.company_name} onChange={e => update('company_name', e.target.value)} className="bg-black/30 border-white/10 text-white" /></Field>
                      <Field label="Industry *">
                        <select data-testid="sponsor-field-industry" value={form.industry} onChange={e => update('industry', e.target.value)} className="w-full h-10 px-3 rounded-md bg-black/30 border border-white/10 text-white text-sm">
                          <option value="">Select…</option>
                          {INDUSTRIES.map(x => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </Field>
                      <Field label="Business Description"><Textarea data-testid="sponsor-field-description" value={form.business_description} onChange={e => update('business_description', e.target.value)} rows={3} className="bg-black/30 border-white/10 text-white" placeholder="What does your business do?" /></Field>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Website"><Input value={form.website} onChange={e => update('website', e.target.value)} className="bg-black/30 border-white/10 text-white" placeholder="https://" /></Field>
                        <Field label="Business Address"><Input value={form.business_address} onChange={e => update('business_address', e.target.value)} className="bg-black/30 border-white/10 text-white" placeholder="City, State" /></Field>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Primary Contact *"><Input data-testid="sponsor-field-contact" value={form.primary_contact} onChange={e => update('primary_contact', e.target.value)} className="bg-black/30 border-white/10 text-white" placeholder="Your name" /></Field>
                        <Field label="Phone"><Input value={form.phone} onChange={e => update('phone', e.target.value)} className="bg-black/30 border-white/10 text-white" placeholder="555-555-5555" /></Field>
                      </div>
                      <Field label="Contact Email *"><Input data-testid="sponsor-field-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} className="bg-black/30 border-white/10 text-white" /></Field>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-4">
                      <p className="text-xs text-gray-400 mb-4">Pick every category of event you&apos;d consider sponsoring.</p>
                      <div className="grid grid-cols-2 gap-2">
                        {INTEREST_OPTIONS.map(opt => (
                          <button key={opt} type="button" onClick={() => toggle('interests', opt)}
                            data-testid={`sponsor-interest-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                            className={`px-3 py-2.5 rounded-lg text-xs font-semibold text-left border transition-all ${form.interests.includes(opt) ? 'bg-blue-500/20 border-blue-500 text-blue-200' : 'bg-black/20 border-white/10 text-gray-300 hover:border-white/30'}`}>
                            {form.interests.includes(opt) ? '✓ ' : ''}{opt}
                          </button>
                        ))}
                      </div>
                      <div className="mt-6">
                        <Label className="text-xs uppercase tracking-widest text-blue-300 font-bold mb-2 block">Business Goals</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {BUSINESS_GOALS.map(g => (
                            <button key={g} type="button" onClick={() => toggle('business_goals', g)}
                              className={`px-3 py-2.5 rounded-lg text-xs font-semibold text-left border transition-all ${form.business_goals.includes(g) ? 'bg-emerald-500/20 border-emerald-500 text-emerald-200' : 'bg-black/20 border-white/10 text-gray-300 hover:border-white/30'}`}>
                              {form.business_goals.includes(g) ? '✓ ' : ''}{g}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <p className="text-xs text-gray-400 mb-2">Where can your business support events?</p>
                      <Field label="Geographic Coverage *">
                        <select data-testid="sponsor-field-coverage" value={form.geographic_coverage} onChange={e => update('geographic_coverage', e.target.value)} className="w-full h-10 px-3 rounded-md bg-black/30 border border-white/10 text-white text-sm">
                          <option value="">Select…</option>
                          {COVERAGE_OPTIONS.map(x => <option key={x} value={x}>{x}</option>)}
                        </select>
                      </Field>
                      <Field label="Travel Radius (miles)"><Input value={form.travel_radius} onChange={e => update('travel_radius', e.target.value)} placeholder="e.g. 100, or 'unlimited'" className="bg-black/30 border-white/10 text-white" /></Field>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-5">
                      <Field label="Sponsorship Budget *">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {BUDGET_OPTIONS.map(b => (
                            <button key={b} type="button" onClick={() => update('budget', b)}
                              data-testid={`sponsor-budget-${b.replace(/[^a-z0-9]/gi, '').toLowerCase()}`}
                              className={`px-3 py-2.5 rounded-lg text-xs font-bold border transition-all ${form.budget === b ? 'bg-blue-500/20 border-blue-500 text-blue-200' : 'bg-black/20 border-white/10 text-gray-300 hover:border-white/30'}`}>
                              {b}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-blue-300 font-bold mb-2 block">Sponsorship Types *</Label>
                        <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                          {SPONSORSHIP_TYPES.map(t => (
                            <button key={t} type="button" onClick={() => toggle('sponsorship_types', t)}
                              className={`px-3 py-2 rounded-lg text-xs font-semibold text-left border transition-all ${form.sponsorship_types.includes(t) ? 'bg-yellow-500/20 border-yellow-500 text-yellow-200' : 'bg-black/20 border-white/10 text-gray-300 hover:border-white/30'}`}>
                              {form.sponsorship_types.includes(t) ? '✓ ' : ''}{t}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-300">Review your application. If everything looks good, submit — a director will review and email you login credentials once approved.</p>
                      <div className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-2 text-xs">
                        <ReviewRow label="Company" value={form.company_name} />
                        <ReviewRow label="Industry" value={form.industry} />
                        <ReviewRow label="Contact" value={`${form.primary_contact} • ${form.email}`} />
                        <ReviewRow label="Phone" value={form.phone} />
                        <ReviewRow label="Interests" value={form.interests.join(', ') || '—'} />
                        <ReviewRow label="Coverage" value={`${form.geographic_coverage}${form.travel_radius ? ` · ${form.travel_radius} mi` : ''}`} />
                        <ReviewRow label="Budget" value={form.budget} />
                        <ReviewRow label="Sponsorship Types" value={form.sponsorship_types.join(', ') || '—'} />
                        <ReviewRow label="Goals" value={form.business_goals.join(', ') || '—'} />
                      </div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
                    <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="border-white/20 text-white hover:bg-white/10 disabled:opacity-30">
                      <ChevronLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    {step < 4 ? (
                      <Button data-testid="sponsor-form-next" onClick={() => setStep(s => s + 1)} disabled={!isStepValid()} className="bg-blue-500 hover:bg-blue-400 text-white font-bold px-6 py-5 rounded-xl disabled:opacity-30">
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button data-testid="sponsor-form-submit" onClick={submit} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 py-5 rounded-xl">
                        {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : (<>Submit Application <ArrowRight className="w-4 h-4 ml-2" /></>)}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-blue-300 font-bold mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex gap-3">
      <span className="text-gray-500 w-36 flex-shrink-0">{label}:</span>
      <span className="text-white break-all">{value || '—'}</span>
    </div>
  );
}
